import { supabase, handleSupabaseError } from '../lib/supabase';
import { mergePricingRuleTranslation } from '../lib/translations';

export interface PricingRule {
  id: string;
  product_type_id: string;
  duration_type: string;
  duration_value: number | null;
  duration_label: string;
  price: number;
  currency: string | null;
  size_specific: boolean | null;
  size: string | null;
  valid_days_of_week: number[] | null;
  valid_time_from: string | null;
  valid_time_until: string | null;
  valid_from: string | null;
  valid_to: string | null;
  display_order: number | null;
  featured: boolean | null;
  active: boolean | null;
  channels: string[];
  flow_type: string | null;
  created_at: string | null;
  updated_at: string | null;
  location_ids: string[] | null;
}

export interface PricingRuleInput {
  product_type_id: string;
  duration_type: string;
  duration_value?: number | null;
  duration_label: string; // Required field
  price: number;
  currency?: string | null;
  size_specific?: boolean | null;
  size?: string | null;
  valid_days_of_week?: number[] | null;
  valid_time_from?: string | null;
  valid_time_until?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  display_order?: number | null;
  featured?: boolean | null;
  active?: boolean | null;
  channels?: string[];
  flow_type?: string | null;
  location_ids?: string[] | null;
}

export async function getPricingRulesForProduct(
  productTypeId: string,
  language: string = 'en',
  defaultLanguage?: string
) {
  try {
    const { data, error } = await supabase
      .from('pricing_rules')
      .select('*')
      .eq('product_type_id', productTypeId)
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Skip translation merge for the default language —
    // default language values are stored in the main table.
    if (!defaultLanguage || language === defaultLanguage) {
      return { data: (data || []) as PricingRule[], error: null };
    }

    // Fetch translations for all pricing rules
    const rulesWithTranslations = await Promise.all(
      (data || []).map(async (rule) => {
        const { data: translation } = await supabase
          .from('pricing_rule_translations')
          .select('*')
          .eq('pricing_rule_id', rule.id)
          .eq('language_code', language)
          .maybeSingle();

        return mergePricingRuleTranslation(rule, translation);
      })
    );

    return { data: rulesWithTranslations as PricingRule[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

export async function createPricingRule(rule: PricingRuleInput) {
  try {
    const { data, error } = await supabase
      .from('pricing_rules')
      .insert(rule)
      .select()
      .single();

    if (error) throw error;

    return { data: data as PricingRule, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

export async function updatePricingRule(id: string, updates: Partial<PricingRuleInput>) {
  try {
    const { data, error } = await supabase
      .from('pricing_rules')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data: data as PricingRule, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

export async function deletePricingRule(id: string) {
  try {
    const { error } = await supabase
      .from('pricing_rules')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

export async function savePricingRuleChanges(
  rules: Array<{
    id?: string;
    product_type_id?: string;
    duration_type?: string;
    duration_value?: number | null;
    duration_label?: string;
    price?: number;
    currency?: string | null;
    size_specific?: boolean | null;
    size?: string | null;
    valid_days_of_week?: number[] | null;
    valid_time_from?: string | null;
    valid_time_until?: string | null;
    valid_from?: string | null;
    valid_to?: string | null;
    display_order?: number | null;
    featured?: boolean | null;
    active?: boolean | null;
    channels?: string[];
    flow_type?: string | null;
    location_ids?: string[] | null;
    _status: 'existing' | 'added' | 'removed';
    _translations?: Record<string, { duration_label: string }>;
    _customLabel?: boolean;
  }>
) {
  const errors: string[] = [];

  // Helper to save translations for a rule
  async function saveTranslations(ruleId: string, translations?: Record<string, { duration_label: string }>) {
    if (!translations) return;
    for (const [langCode, translation] of Object.entries(translations)) {
      if (translation.duration_label) {
        const { error } = await upsertPricingRuleTranslation(ruleId, langCode, translation);
        if (error) errors.push(`Failed to save translation (${langCode}): ${error}`);
      }
    }
  }

  // Process removed rules (only those with real IDs, not temp IDs)
  const toRemove = rules.filter(r => r._status === 'removed' && r.id && !r.id.startsWith('temp-'));
  for (const rule of toRemove) {
    const { error } = await deletePricingRule(rule.id!);
    if (error) errors.push(`Failed to remove rule: ${error}`);
  }

  // Process added rules
  const toAdd = rules.filter(r => r._status === 'added');
  for (const rule of toAdd) {
    const { id, _status, _translations, _customLabel, ...ruleData } = rule;
    const { data: created, error } = await createPricingRule(ruleData as PricingRuleInput);
    if (error) {
      errors.push(`Failed to add rule: ${error}`);
    } else if (created) {
      await saveTranslations(created.id, _translations);
    }
  }

  // Process updated existing rules (only those with real IDs)
  const toUpdate = rules.filter(r => r._status === 'existing' && r.id && !r.id.startsWith('temp-'));
  for (const rule of toUpdate) {
    const { id, _status, _translations, _customLabel, ...updates } = rule;
    const { error } = await updatePricingRule(id!, updates as Partial<PricingRuleInput>);
    if (error) {
      errors.push(`Failed to update rule: ${error}`);
    } else {
      await saveTranslations(id!, _translations);
    }
  }

  if (errors.length > 0) return { error: errors.join('; ') };
  return { error: null };
}

/**
 * Upsert pricing rule translation
 * Creates or updates a translation for a specific language
 */
export async function upsertPricingRuleTranslation(
  pricingRuleId: string,
  languageCode: string,
  translation: {
    duration_label?: string | null;
  }
) {
  try {
    const { data, error } = await supabase
      .from('pricing_rule_translations')
      .upsert(
        {
          pricing_rule_id: pricingRuleId,
          language_code: languageCode,
          ...translation,
        },
        {
          onConflict: 'pricing_rule_id,language_code',
        }
      )
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get all translations for a pricing rule
 */
export async function getPricingRuleTranslations(pricingRuleId: string) {
  try {
    const { data, error } = await supabase
      .from('pricing_rule_translations')
      .select('*')
      .eq('pricing_rule_id', pricingRuleId);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get all translations for multiple pricing rules in a single query
 */
export async function getPricingRuleTranslationsBatch(pricingRuleIds: string[]) {
  if (pricingRuleIds.length === 0) return { data: {}, error: null };

  try {
    const { data, error } = await supabase
      .from('pricing_rule_translations')
      .select('*')
      .in('pricing_rule_id', pricingRuleIds);

    if (error) throw error;

    // Group by pricing_rule_id → language_code
    const grouped: Record<string, Record<string, { duration_label: string }>> = {};
    for (const row of data || []) {
      if (!grouped[row.pricing_rule_id]) grouped[row.pricing_rule_id] = {};
      grouped[row.pricing_rule_id][row.language_code] = {
        duration_label: row.duration_label || '',
      };
    }

    return { data: grouped, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
